import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Column } from "primereact/column";
import localeText from "../../../../locale";
import ProductService from "../../../../service/ProductService";
import { formatPrice } from "../../../../utilities/formatPrice";
import useAdminList, { expectCode } from "../../hooks/useAdminList";
import { buildSearchText } from "../../utils/adminUtils";
import CatalogTable from "../../components/list/CatalogTable";
import CatalogThumb from "../../components/list/CatalogThumb";

const loadProducts = async () => expectCode(await new ProductService().getAllProducts(), 200001).result;

const discountPercent = (product) =>
  product.promotionalPriceUnique && product.priceUnique
    ? Math.round(100 - (product.promotionalPriceUnique * 100) / product.priceUnique)
    : 0;

const AdminProducts = () => {
  const { items, loading, loadError, reload } = useAdminList(loadProducts);

  const rows = useMemo(
    () =>
      items.map((product) => ({
        ...product,
        sellerUsername: product.user?.username,
        effectivePrice: product.promotionalPriceUnique || product.priceUnique || 0,
        searchText: buildSearchText([String(product.id ?? ""), product.title, product.user?.username]),
      })),
    [items],
  );

  const priceTemplate = (product) => {
    const currency = product.currency || "";
    if (!product.promotionalPriceUnique) {
      return <span className="admin-num">{`${currency} ${formatPrice(product.priceUnique)}`}</span>;
    }
    return (
      <div className="admin-stack">
        <span className="admin-num admin-strong">
          {`${currency} ${formatPrice(product.promotionalPriceUnique)}`}
          <span className="admin-chip admin-chip--locked admin-products__discount">
            -{discountPercent(product)}%
          </span>
        </span>
        <s className="admin-num admin-muted">{`${currency} ${formatPrice(product.priceUnique)}`}</s>
      </div>
    );
  };

  return (
    <div className="admin-list">
      <CatalogTable
        rows={rows}
        loading={loading}
        loadError={loadError}
        onReload={reload}
        searchPlaceholder={localeText("adminProductsSearch")}
        sortField="id"
        sortOrder={-1}
        minWidth="56rem"
      >
        <Column
          header={localeText("adverts")}
          field="title"
          body={(product) => (
            <div className="admin-catalog__item">
              <CatalogThumb imageId={product.imageid1} icon="pi pi-box" />
              <div className="admin-stack">
                <span className="admin-strong">{product.title || "—"}</span>
                <span className="admin-muted">#{product.id}</span>
              </div>
            </div>
          )}
          sortable
        />
        <Column
          header={localeText("seller")}
          field="sellerUsername"
          body={(product) =>
            product.sellerUsername ? (
              <span>@{product.sellerUsername}</span>
            ) : (
              <span className="admin-muted">—</span>
            )
          }
          sortable
        />
        <Column header={localeText("price")} field="effectivePrice" body={priceTemplate} sortable />
        <Column
          header={localeText("productState")}
          field="stateofproduct"
          body={(product) =>
            product.stateofproduct === "NEW"
              ? localeText("new")
              : product.stateofproduct === "USED"
                ? localeText("used")
                : "—"
          }
          sortable
        />
        <Column
          header={localeText("stock")}
          field="amountInStock"
          body={(product) =>
            product.amountInStock > 0 ? (
              <span className="admin-num">{product.amountInStock}</span>
            ) : (
              <span className="admin-badge admin-badge--warning">
                <i className="pi pi-exclamation-circle" aria-hidden="true" />
                {product.amountInStock ?? 0}
              </span>
            )
          }
          sortable
        />
        <Column
          header={localeText("actions")}
          frozen
          alignFrozen="right"
          headerClassName="admin-list__actions-cell"
          bodyClassName="admin-list__actions-cell"
          body={(product) => (
            <Link
              className="admin-btn"
              to={`/product/${product.id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="pi pi-external-link" aria-hidden="true" />
              {localeText("adminCatalogViewInStore")}
            </Link>
          )}
        />
      </CatalogTable>
    </div>
  );
};

export default AdminProducts;
